import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Calendar, MapPin, Users, Euro, ArrowRight, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicActivities = () => {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [logoUrl, setLogoUrl] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API_URL}/api/public/activities`);
                setActivities(res.data || []);
            } catch { /* keep empty list; banner handles it */ }
            finally { setLoading(false); }
            setLogoUrl(`${API_URL}/api/public/logo`);
        })();
    }, []);

    const closingBadge = (a) => {
        const closing = a.closing_date || '';
        if (!closing) return <Badge variant="outline">Open</Badge>;
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const closingDate = new Date(`${closing}T00:00:00`);
        const days = Math.ceil((closingDate - today) / 86400000);
        if (days < 0) return <Badge className="bg-[#6B7280] text-white">Closed</Badge>;
        if (days <= 3) return <Badge className="bg-[#EF4444] text-white">Closes in {days}d</Badge>;
        if (days <= 7) return <Badge className="bg-[#F59E0B] text-white">Closes in {days}d</Badge>;
        return <Badge variant="outline">Open</Badge>;
    };

    return (
        <div className="min-h-screen bg-[#FAF9F6]" data-testid="public-activities-page">
            <header className="bg-white border-b border-[#E5E7EB]">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img
                            src={logoUrl}
                            alt="Nurulhuda Quranic School"
                            className="h-12 w-auto"
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <div>
                            <h1 className="text-lg font-semibold text-[#064E3B] font-['Outfit']">Nurulhuda Quranic School</h1>
                            <p className="text-xs text-[#4B5563]">Public Activity Registration</p>
                        </div>
                    </div>
                    <Link to="/login">
                        <Button variant="outline" size="sm" data-testid="public-login-link">Already a guardian? Log in</Button>
                    </Link>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 py-10">
                <div className="mb-8">
                    <h2 className="text-3xl font-bold text-[#1F2937] font-['Outfit'] mb-2">Open Activities</h2>
                    <p className="text-[#4B5563]">
                        Browse activities open to the general public — including non-enrolled families.
                        Register and pay online to secure your child&rsquo;s place.
                    </p>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#064E3B]" /></div>
                ) : activities.length === 0 ? (
                    <Card className="card-surface">
                        <CardContent className="py-16 text-center">
                            <Calendar className="h-12 w-12 text-[#4B5563]/30 mx-auto mb-4" />
                            <p className="text-[#4B5563] mb-2">No public activities are open right now.</p>
                            <p className="text-xs text-[#9CA3AF]">Please check back soon or contact the school for more details.</p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {activities.map((a) => (
                            <Card key={a.id} className="card-surface hover:shadow-md transition-shadow overflow-hidden" data-testid={`public-activity-card-${a.id}`}>
                                {a.image_url ? (
                                    <div className="w-full h-40 bg-[#F3F4F6] overflow-hidden">
                                        <img
                                            src={a.image_url}
                                            alt={a.name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                                        />
                                    </div>
                                ) : null}
                                <CardContent className="p-5 space-y-3">
                                    <div className="flex items-start justify-between gap-2">
                                        <h3 className="font-semibold text-[#1F2937] text-base leading-tight">{a.name}</h3>
                                        {closingBadge(a)}
                                    </div>
                                    {a.description && <p className="text-xs text-[#4B5563] line-clamp-3">{a.description}</p>}
                                    <div className="grid grid-cols-2 gap-2 text-xs text-[#4B5563]">
                                        <div className="flex items-start gap-1.5"><Calendar className="h-3.5 w-3.5 mt-0.5 text-[#064E3B]" /><span className="tabular-nums">{a.start_date} → {a.end_date}</span></div>
                                        <div className="flex items-start gap-1.5"><Euro className="h-3.5 w-3.5 mt-0.5 text-[#064E3B]" /><span className="font-semibold">€{Number(a.cost || 0).toFixed(2)}{a.deposit ? ` (deposit €${Number(a.deposit).toFixed(2)})` : ''}</span></div>
                                        {a.location && <div className="flex items-start gap-1.5 col-span-2"><MapPin className="h-3.5 w-3.5 mt-0.5 text-[#064E3B]" /><span>{a.location}</span></div>}
                                        {(a.min_age != null || a.max_age != null) && <div className="flex items-start gap-1.5"><Users className="h-3.5 w-3.5 mt-0.5 text-[#064E3B]" /><span>Ages {a.min_age ?? '?'}&ndash;{a.max_age ?? '?'}</span></div>}
                                        {a.gender && a.gender !== 'both' && <div className="flex items-start gap-1.5"><Users className="h-3.5 w-3.5 mt-0.5 text-[#064E3B]" /><span className="capitalize">{a.gender} only</span></div>}
                                    </div>
                                    <Link to={`/public/activities/${a.id}`} className="block">
                                        <Button className="w-full bg-[#064E3B] hover:bg-[#047857] text-white" data-testid={`public-register-btn-${a.id}`}>
                                            Register Now <ArrowRight className="h-4 w-4 ml-2" />
                                        </Button>
                                    </Link>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </main>

            <footer className="border-t border-[#E5E7EB] bg-white mt-12">
                <div className="max-w-5xl mx-auto px-6 py-6 text-center text-xs text-[#6B7280]">
                    Questions? Email <a href="mailto:info@nhq.ie" className="text-[#064E3B] underline">info@nhq.ie</a>
                </div>
            </footer>
        </div>
    );
};

export default PublicActivities;
